/*
 * Deliveries and booking in.
 *
 * The pharmacy makes up the list of what it is sending. When the box arrives the home goes down
 * it, ticking each item off or saying what was wrong with it. Nothing is finished until every
 * line has been looked at, and controlled drugs need a second person.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { User, Witness } from './api.ts';

type Delivery = {
  id: string;
  kind: 'cycle' | 'interim';
  cycleStart: string | null;
  reference: string;
  note: string;
  status: 'draft' | 'sent' | 'received';
  createdAt: string;
  createdBy: string;
  sentAt: string | null;
  bookedInAt: string | null;
  bookedInBy: string;
  witness: string;
  problems: number;
  lines: number;
  checked: number;
};

type Line = {
  id: string;
  residentName: string;
  room: string;
  medicineId: string | null;
  controlledDrug: boolean;
  kind: 'tray' | 'pack' | 'other';
  description: string;
  detail: string;
  expectedQty: number;
  unit: string;
  receivedQty: number | null;
  outcome: 'ok' | 'short' | 'extra' | 'missing' | 'damaged' | 'not-ordered' | null;
  note: string;
  checkedBy: string;
};

const PROBLEMS: Array<[Line['outcome'], string]> = [
  ['short', 'Not enough'],
  ['missing', 'Did not arrive'],
  ['damaged', 'Damaged'],
  ['extra', 'More than expected'],
  ['not-ordered', 'We did not order this'],
];

export function DeliveriesScreen({
  homeId, user, initialOpenId = null,
}: { homeId: string; user: User; initialOpenId?: string | null }) {
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  // The pharmacy's list of every delivery opens straight onto the one that was picked.
  const [openId, setOpenId] = useState<string | null>(initialOpenId);
  const [error, setError] = useState('');
  const isPharmacy = user.roles.includes('pharmacy');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ deliveries: Delivery[] }>(`/api/homes/${homeId}/deliveries`);
      setDeliveries(data.deliveries);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the deliveries.');
    }
  }, [homeId]);

  useEffect(() => {
    if (!openId) void load();
  }, [load, openId]);

  const makeUp = useCallback(
    async (kind: 'cycle' | 'interim') => {
      try {
        const made = await api.post<{ id: string }>(`/api/homes/${homeId}/deliveries`, { kind, cycle: 'next' });
        setOpenId(made.id);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
      }
    },
    [homeId],
  );

  if (openId) {
    return <OneDelivery deliveryId={openId} homeId={homeId} user={user} onBack={() => setOpenId(null)} />;
  }

  return (
    <section>
      <div className="head">
        <h1>{isPharmacy ? 'Deliveries' : 'Booking in'}</h1>
        {isPharmacy ? (
          <div className="row-actions">
            <button type="button" onClick={() => void makeUp('cycle')}>Make up the next cycle</button>
            <button type="button" className="quiet" onClick={() => void makeUp('interim')}>Something in between</button>
          </div>
        ) : null}
      </div>
      <p className="muted">
        {isPharmacy
          ? 'What we are sending, and what the home found when they checked it.'
          : 'Check what arrived against what the pharmacy says it sent. Anything that does not match goes back to them.'}
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {deliveries === null ? <p className="muted">Loading…</p> : null}
      {deliveries && !deliveries.length ? <p className="notice">Nothing to book in.</p> : null}

      <ul className="list">
        {(deliveries ?? []).map((delivery) => (
          <li key={delivery.id}>
            <button type="button" className="row column" onClick={() => setOpenId(delivery.id)}>
              <div className="row-line">
                <span className="row-main">
                  {delivery.kind === 'cycle' ? `Cycle starting ${formatDate(delivery.cycleStart ?? '')}` : 'In-between delivery'}
                </span>
                <span className="muted small">{formatWhen(delivery.sentAt ?? delivery.createdAt)}</span>
              </div>
              <div className="chips">
                <span className={`chip ${delivery.status === 'received' ? 'done' : ''}`}>{statusText(delivery)}</span>
                <span className="chip">{delivery.lines} item{delivery.lines === 1 ? '' : 's'}</span>
                {delivery.problems ? (
                  <span className="chip warn">{delivery.problems} did not match</span>
                ) : null}
                {delivery.reference ? <span className="chip">{delivery.reference}</span> : null}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function OneDelivery({
  deliveryId,
  homeId,
  user,
  onBack,
}: {
  deliveryId: string;
  homeId: string;
  user: User;
  onBack: () => void;
}) {
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [witnessId, setWitnessId] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState<number | null>(null);
  const isPharmacy = user.roles.includes('pharmacy');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ delivery: Delivery; lines: Line[] }>(`/api/deliveries/${deliveryId}`);
      setDelivery(data.delivery);
      setLines(data.lines);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that delivery.');
    }
  }, [deliveryId]);

  useEffect(() => {
    void load();
    api
      .get<{ witnesses: Witness[] }>(`/api/homes/${homeId}/witnesses`)
      .then((data) => setWitnesses(data.witnesses))
      .catch(() => setWitnesses([]));
  }, [load, homeId]);

  const check = useCallback(
    async (line: Line, outcome: NonNullable<Line['outcome']>) => {
      let receivedQty: number | undefined;
      let note = '';
      if (outcome === 'short' || outcome === 'extra') {
        const answer = prompt(`How many of "${line.description}" actually arrived?`, String(line.expectedQty));
        if (answer === null) return;
        receivedQty = Number(answer);
        if (!Number.isFinite(receivedQty)) return;
      }
      if (outcome !== 'ok') {
        note = prompt('Anything to add? (optional)', line.note) ?? '';
      }
      try {
        await api.post(`/api/deliveries/${deliveryId}/lines/${line.id}`, { outcome, receivedQty, note });
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      }
    },
    [deliveryId, load],
  );

  const send = useCallback(async () => {
    try {
      await api.post(`/api/deliveries/${deliveryId}/send`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send.');
    }
  }, [deliveryId, load]);

  const finish = useCallback(async () => {
    try {
      const result = await api.post<{ problems: number }>(`/api/deliveries/${deliveryId}/book-in`, {
        witnessId: witnessId || undefined,
      });
      setDone(result.problems);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.');
    }
  }, [deliveryId, witnessId, load]);

  const anyControlled = lines.some((line) => line.controlledDrug && line.outcome !== 'missing');
  const allChecked = lines.length > 0 && lines.every((line) => line.outcome);
  const canBookIn = delivery?.status === 'sent' && !isPharmacy;

  return (
    <section>
      <div className="head">
        <div>
          <h1>
            {delivery?.kind === 'cycle'
              ? `Cycle starting ${formatDate(delivery.cycleStart ?? '')}`
              : 'In-between delivery'}
          </h1>
          <p className="muted small">
            {delivery ? statusText(delivery) : ''}
            {delivery?.reference ? ` · ${delivery.reference}` : ''}
          </p>
        </div>
        <button type="button" className="quiet" onClick={onBack}>Back</button>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {done !== null ? (
        <p className={`notice ${done ? 'warn' : ''}`}>
          {done
            ? `Booked in. ${done} item${done === 1 ? '' : 's'} did not match, and the pharmacy can see exactly which.`
            : 'Booked in, everything matched.'}
        </p>
      ) : null}

      {delivery?.status === 'received' ? (
        <p className="muted">
          Booked in by {delivery.bookedInBy} at {formatWhen(delivery.bookedInAt ?? '')}
          {delivery.witness ? `, with ${delivery.witness}` : ''}.
        </p>
      ) : null}

      <ul className="list">
        {lines.map((line) => (
          <li key={line.id}>
            <div className={`row static column ${line.outcome && line.outcome !== 'ok' ? 'dose overdue' : ''}`}>
              <div className="row-line">
                <span className="row-main">{line.description}</span>
                <span className="time">
                  {line.expectedQty} {line.unit}
                </span>
              </div>
              <p className="muted small">
                {line.residentName}
                {line.room ? ` · Room ${line.room}` : ''}
                {line.detail ? ` · ${line.detail}` : ''}
              </p>

              <div className="chips">
                {line.kind === 'tray' ? <span className="chip">Tray card</span> : null}
                {line.controlledDrug ? <span className="chip">Controlled drug</span> : null}
                {line.outcome ? (
                  <span className={`chip ${line.outcome === 'ok' ? 'done' : 'warn'}`}>
                    {line.outcome === 'ok'
                      ? `Checked by ${line.checkedBy}`
                      : `${PROBLEMS.find(([id]) => id === line.outcome)?.[1] ?? line.outcome}${
                          line.receivedQty !== null && line.outcome === 'short' ? `: ${line.receivedQty} arrived` : ''
                        }`}
                  </span>
                ) : null}
                {line.note ? <span className="chip">{line.note}</span> : null}
              </div>

              {canBookIn ? (
                <div className="row-actions">
                  <button type="button" onClick={() => void check(line, 'ok')}>
                    {line.outcome === 'ok' ? 'All there ✓' : 'All there'}
                  </button>
                  {PROBLEMS.map(([outcome, label]) => (
                    <button key={outcome} type="button" className="quiet"
                      onClick={() => void check(line, outcome as NonNullable<Line['outcome']>)}>
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {delivery?.status === 'draft' && isPharmacy ? (
        <div className="card wide">
          <h2>Ready to go?</h2>
          <p className="muted">Once it is sent, the home can see the list and check the box against it.</p>
          <button type="button" onClick={() => void send()}>Send this to the home</button>
        </div>
      ) : null}

      {canBookIn ? (
        <div className="card wide">
          <h2>Finish booking in</h2>
          <p className="muted">
            {allChecked
              ? 'Every item has been looked at.'
              : `${lines.filter((l) => !l.outcome).length} still to check.`}
          </p>

          {anyControlled ? (
            <>
              <label htmlFor="d-witness">Second person, for the controlled drugs</label>
              <select id="d-witness" required value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
                <option value="">Choose who counted them with you</option>
                {witnesses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name} — {w.jobTitle}</option>
                ))}
              </select>
              <p className="muted small">They go straight into the controlled drugs register.</p>
            </>
          ) : null}

          <button type="button" disabled={!allChecked || (anyControlled && !witnessId)} onClick={() => void finish()}>
            Book this delivery in
          </button>
        </div>
      ) : null}
    </section>
  );
}

function statusText(delivery: Delivery): string {
  if (delivery.status === 'draft') return 'Being made up';
  if (delivery.status === 'sent') return `On its way · ${delivery.checked} of ${delivery.lines} checked`;
  return delivery.problems ? `Booked in, ${delivery.problems} did not match` : 'Booked in, all correct';
}

function formatDate(date: string): string {
  if (!date) return '';
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatWhen(iso: string): string {
  if (!iso) return '';
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)} ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
